import { Head, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'

function HeartMark({ className = 'h-6 w-6' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12 20.3s-7.5-4.5-7.5-10.1c0-2.6 2-4.7 4.6-4.7 1.6 0 3 .8 3.9 2 .9-1.2 2.3-2 3.9-2 2.6 0 4.6 2.1 4.6 4.7 0 5.6-7.5 10.1-7.5 10.1z" />
    </svg>
  )
}

function Brand({ size = 'md' }) {
  const isLg = size === 'lg'
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`inline-flex items-center justify-center rounded-full bg-blue-800 text-white ${
          isLg ? 'h-9 w-9' : 'h-8 w-8'
        }`}
      >
        <HeartMark className={isLg ? 'h-5 w-5' : 'h-4 w-4'} />
      </span>
      <span className={`font-bold tracking-tight text-slate-900 ${isLg ? 'text-2xl' : 'text-xl'}`}>
        Pop Health
      </span>
    </span>
  )
}

function Nav() {
  return (
    <header className="border-b border-slate-200 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70 sticky top-0 z-50">
      <div className="container mx-auto flex items-center justify-between px-4 py-4">
        <Brand />
        <div className="flex items-center gap-3">
          <Link href="/training">
            <Button variant="ghost" size="sm">Training</Button>
          </Link>
          <Link href="/users/sign_in">
            <Button variant="ghost" size="sm">Sign in</Button>
          </Link>
          <Link href="/users/sign_up">
            <Button
              size="sm"
              className="rounded-full bg-blue-800 px-4 text-white hover:bg-blue-900"
            >
              Get started
            </Button>
          </Link>
        </div>
      </div>
    </header>
  )
}

function Hero({ hasAgents }) {
  return (
    <section className="relative overflow-hidden py-20 md:py-28">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl" />
        <div className="absolute top-10 right-0 h-80 w-80 rounded-full bg-emerald-100/60 blur-3xl" />
      </div>
      <div className="container mx-auto px-4 text-center">
        <div className="mx-auto max-w-3xl space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold tracking-wide text-blue-800">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Beta — early access
          </span>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
            Real-time video assistants{' '}
            <span className="text-blue-800">for every step of care</span>
          </h1>
          <p className="text-lg text-slate-600 md:text-xl max-w-2xl mx-auto">
            Try any of our friendly virtual care assistants below. Each one is purpose-built
            for a specific patient journey — pick one and start a live video call.
          </p>
          {hasAgents && (
            <div className="pt-2">
              <a href="#agents">
                <Button
                  size="lg"
                  className="h-12 rounded-full bg-blue-800 px-7 text-base text-white shadow-sm hover:bg-blue-900"
                >
                  Browse assistants
                </Button>
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function AgentCard({ agent }) {
  return (
    <Link
      href={agent.url}
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm ring-1 ring-transparent transition hover:-translate-y-0.5 hover:shadow-md hover:ring-blue-200"
    >
      <div className="flex items-start gap-4">
        {agent.avatarPreviewUrl ? (
          <img
            src={agent.avatarPreviewUrl}
            alt=""
            className="h-12 w-12 rounded-full object-cover ring-2 ring-white shadow-sm"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-800">
            <HeartMark className="h-5 w-5" />
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-lg font-semibold leading-tight text-slate-900">{agent.name}</h3>
          {agent.description && (
            <p className="mt-1 text-sm text-slate-600">{agent.description}</p>
          )}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500">
          Live video assistant
        </span>
        <span className="text-sm font-semibold text-blue-800 group-hover:underline">
          Try it →
        </span>
      </div>
    </Link>
  )
}

function AgentsGrid({ agents }) {
  if (!agents?.length) return null
  return (
    <section id="agents" className="pb-24">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl font-bold tracking-tight text-center mb-2 text-slate-900">
          Pick an assistant to try
        </h2>
        <p className="text-center text-slate-600 mb-10">
          Each card opens a live video call with that assistant.
        </p>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
          {agents.map((agent) => (
            <AgentCard key={agent.url} agent={agent} />
          ))}
        </div>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white py-8">
      <div className="container mx-auto px-4 flex flex-col items-center gap-2 text-sm text-slate-500">
        <Brand />
        <span>&copy; {new Date().getFullYear()} Pop Health. Made with care.</span>
      </div>
    </footer>
  )
}

export default function Landing({ agents = [] }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Head title="Pop Health" />
      <Nav />
      <main className="flex-1">
        <Hero hasAgents={agents.length > 0} />
        <AgentsGrid agents={agents} />
      </main>
      <Footer />
    </div>
  )
}
